import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { blogs } from '../data/blogsData';
import { FAQitem } from '../utils/FAQitem';

const Blog6 = () => {
    const navigate = useNavigate();
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const blogData = blogs.find(b => b.id === 6);

    return (
        <div className="bg-white">
            <div className="max-w-[1180px] mx-auto px-5 py-5 text-left">
                <button
                    onClick={() => navigate('/blogs')}
                    className="inline-flex fixed top-30 bg-black p-1 rounded-full hover:scale-110 items-center gap-2 text-gray-500 hover:text-gray-900 transition-colors text-sm font-medium mb-5"
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="19" y1="12" x2="5" y2="12"></line>
                        <polyline points="12 19 5 12 12 5"></polyline>
                    </svg>
                </button>
            </div>

            <div className="max-w-[1180px] mx-auto px-5 pb-[70px] flex flex-col lg:flex-row items-start gap-10">
                <main className="flex-1 max-w-full lg:max-w-[760px] min-w-0">
                    <section className="pb-6 border-b border-gray-200 mb-7">
                        <h1 className="text-3xl md:text-4xl lg:text-[42px] leading-[1.15] mb-3.5 text-gray-900 font-bold tracking-tight">
                            Used Car Inspection Checklist: 15 Things to Check Before Buying a Used Car
                        </h1>

                        <p className="text-lg md:text-xl text-gray-600 mb-[18px]">
                            Buying a used car can save you money, but only if you know what to look
                            for. This checklist helps you spot common warning signs before you commit
                            to the wrong car.
                        </p>

                        <div className="text-sm text-gray-500">
                            By <strong className="text-gray-900">CarFido Editorial Team</strong>
                            &nbsp;|&nbsp; Updated March 2026 &nbsp;|&nbsp; 12 min read
                        </div>
                    </section>

                    <figure className="mb-8">
                        <img
                            src="/blogs/6_1.jpeg"
                            alt="Inspecting a used car before purchase"
                            title="Used Car Inspection Checklist"
                            className="w-full h-auto block border border-gray-300 rounded-xl"
                        />
                        <figcaption className="text-xs text-gray-400 mt-2">
                            Featured image: used_car_inspection_featured.png
                        </figcaption>
                    </figure>

                    <nav className="bg-gray-50 border border-gray-200 rounded-xl p-5 mb-8">
                        <div className="text-base font-bold text-gray-900 mb-2.5">
                            In this article
                        </div>

                        <ul className="m-0 pl-[18px] text-gray-700 list-disc space-y-1">
                            <li><a href="#quick-answer" className="text-gray-900 no-underline hover:underline">Quick answer</a></li>
                            <li><a href="#inspection-table" className="text-gray-900 no-underline hover:underline">Used car checklist summary</a></li>
                            <li><a href="#why-it-matters" className="text-gray-900 no-underline hover:underline">Why inspecting a used car matters</a></li>
                            <li><a href="#check-1" className="text-gray-900 no-underline hover:underline">Body and paint condition</a></li>
                            <li><a href="#check-2" className="text-gray-900 no-underline hover:underline">Tires</a></li>
                            <li><a href="#check-3" className="text-gray-900 no-underline hover:underline">Glass and lights</a></li>
                            <li><a href="#check-4" className="text-gray-900 no-underline hover:underline">Signs of rust</a></li>
                            <li><a href="#check-5" className="text-gray-900 no-underline hover:underline">Engine bay</a></li>
                            <li><a href="#check-6" className="text-gray-900 no-underline hover:underline">Engine oil and fluids</a></li>
                            <li><a href="#check-7" className="text-gray-900 no-underline hover:underline">Battery condition</a></li>
                            <li><a href="#check-8" className="text-gray-900 no-underline hover:underline">Interior condition</a></li>
                            <li><a href="#check-9" className="text-gray-900 no-underline hover:underline">AC and electronics</a></li>
                            <li><a href="#check-10" className="text-gray-900 no-underline hover:underline">Dashboard warning lights</a></li>
                            <li><a href="#check-11" className="text-gray-900 no-underline hover:underline">Brakes</a></li>
                            <li><a href="#check-12" className="text-gray-900 no-underline hover:underline">Suspension and steering</a></li>
                            <li><a href="#check-13" className="text-gray-900 no-underline hover:underline">Mileage and wear</a></li>
                            <li><a href="#check-14" className="text-gray-900 no-underline hover:underline">Service history</a></li>
                            <li><a href="#check-15" className="text-gray-900 no-underline hover:underline">Test drive</a></li>
                            <li><a href="#dash-section" className="text-gray-900 no-underline hover:underline">Evaluate a used car with CarFido</a></li>
                            <li><a href="#faq" className="text-gray-900 no-underline hover:underline">FAQ</a></li>
                        </ul>
                    </nav>

                    <section className="mb-10 overflow-hidden">
                        <p className="mb-[18px]">
                            Buying a used car can feel exciting at first. You find a car that looks
                            good, the price seems reasonable, and the seller says it has been “well
                            maintained.” But once money changes hands, any hidden problems become
                            your responsibility.
                        </p>

                        <p className="mb-[18px]">
                            That is why a proper used car inspection matters. A clean exterior does
                            not always mean the car is in good condition, and a short drive around the
                            block may not reveal deeper issues with the brakes, engine, suspension, or
                            electronics.
                        </p>

                        <p className="m-0 text-gray-700">
                            This used car inspection checklist will help you slow down and inspect the
                            car more carefully. Whether you are buying from a dealer, an independent
                            seller, or someone you know, these 15 checks can help you avoid expensive
                            surprises.
                        </p>
                    </section>

                    <section id="quick-answer" className="mb-9 bg-gray-50 border-l-4 border-gray-900 rounded-lg p-[18px]">
                        <div className="text-sm font-bold text-gray-500 uppercase tracking-widest mb-2">Quick Answer</div>
                        <p className="m-0 text-gray-900 font-medium">
                            <strong>What should you check before buying a used car?</strong>
                            <br />
                            Before buying a used car, inspect the body, tires, engine bay, fluids,
                            battery, brakes, suspension, interior, warning lights, service records,
                            and overall performance during a test drive.
                        </p>
                    </section>

                    <section id="inspection-table" className="mb-10">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">
                            Used Car Checklist Summary
                        </h2>

                        <div className="overflow-x-auto border border-gray-200 rounded-xl">
                            <table className="w-full border-collapse bg-white text-sm">
                                <thead className="bg-gray-50 text-gray-900 font-bold">
                                    <tr>
                                        <th className="text-left py-3.5 px-4 border-b border-gray-200">Category</th>
                                        <th className="text-left py-3.5 px-4 border-b border-gray-200">What to Look For</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr className="hover:bg-gray-50 transition-colors">
                                        <td className="py-3.5 px-4 border-b border-gray-200 font-medium">Exterior</td>
                                        <td className="py-3.5 px-4 border-b border-gray-200 text-gray-600">Paint issues, dents, rust, uneven panel gaps</td>
                                    </tr>
                                    <tr className="hover:bg-gray-50 transition-colors">
                                        <td className="py-3.5 px-4 border-b border-gray-200 font-medium">Mechanical</td>
                                        <td className="py-3.5 px-4 border-b border-gray-200 text-gray-600">Leaks, fluids, battery, engine condition</td>
                                    </tr>
                                    <tr className="hover:bg-gray-50 transition-colors">
                                        <td className="py-3.5 px-4 border-b border-gray-200 font-medium">Safety and driving</td>
                                        <td className="py-3.5 px-4 border-b border-gray-200 text-gray-600">
                                            Brakes, steering, suspension, warning lights
                                        </td>
                                    </tr>
                                    <tr className="hover:bg-gray-50 transition-colors">
                                        <td className="py-3.5 px-4 font-medium">History</td>
                                        <td className="py-3.5 px-4 text-gray-600">
                                            Mileage, service records, overall care
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </section>

                    <section id="why-it-matters" className="mb-11">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">
                            Why Inspecting a Used Car Matters
                        </h2>

                        <p className="mb-4 text-gray-700">
                            A used car can seem like a great deal until you discover hidden issues a
                            week later. Worn brakes, coolant leaks, electrical problems, accident
                            damage, or neglected maintenance can quickly turn a cheap purchase into an
                            expensive one.
                        </p>

                        <p className="mb-6 text-gray-700">
                            A proper inspection helps you do three important things: understand the
                            real condition of the car, avoid major red flags, and negotiate with more
                            confidence if repairs are needed.
                        </p>

                        <ul className="pl-6 m-0 list-disc space-y-2 text-gray-700">
                            <li>Helps you avoid hidden mechanical problems</li>
                            <li>Makes it easier to spot signs of poor maintenance</li>
                            <li>Gives you stronger negotiation leverage</li>
                            <li>Reduces the chance of buyer’s remorse after purchase</li>
                        </ul>
                    </section>

                    <figure className="mb-10">
                        <img
                            src="/blogs/6_2.jpeg"
                            alt="Inspecting the exterior of a used car"
                            title="Checking used car exterior condition"
                            className="w-full h-auto block border border-gray-300 rounded-xl"
                        />
                        <figcaption className="text-xs text-gray-400 mt-2">
                            Inline image: used_car_exterior_check.png
                        </figcaption>
                    </figure>

                    <section id="check-1" className="mb-11 pt-[1px]">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">1. Check the Body and Paint Condition</h2>
                        <p className="m-0 text-gray-700">
                            Walk around the car slowly and look closely at the paint, doors, bumpers,
                            and panels. Mismatched paint, uneven panel gaps, or dents can suggest
                            previous accident repairs. Small cosmetic flaws are common on older cars,
                            but obvious body inconsistencies deserve more attention.
                        </p>
                    </section>

                    <section id="check-2" className="mb-11 pt-[1px]">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">2. Inspect the Tires</h2>
                        <p className="m-0 text-gray-700">
                            Tire condition says a lot about how the car has been maintained. Check
                            tread depth, tire age if possible, and whether the wear is even across all
                            four tires. Uneven wear can point to poor alignment, suspension issues, or
                            neglect.
                        </p>
                    </section>

                    <section id="check-3" className="mb-11 pt-[1px]">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">3. Look at the Glass and Exterior Lights</h2>
                        <p className="m-0 text-gray-700">
                            Check the windshield for cracks or chips and make sure the headlights,
                            tail lights, and indicators are intact. Damaged lights or glass can add
                            repair costs right away, and sometimes they also suggest the car was not
                            cared for properly.
                        </p>
                    </section>

                    <section id="check-4" className="mb-11 pt-[1px]">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">4. Check for Rust</h2>
                        <p className="m-0 text-gray-700">
                            Surface rust may be minor, but deeper rust around wheel arches, door
                            bottoms, underbody edges, or suspension mounting areas can be a more
                            serious concern. Rust is especially important to check on older vehicles or
                            cars from wet climates.
                        </p>
                    </section>

                    <section id="check-5" className="mb-11 pt-[1px]">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">5. Inspect the Engine Bay</h2>
                        <p className="m-0 text-gray-700">
                            Open the hood and look for leaks, loose wiring, broken plastic parts, or
                            anything that looks recently cleaned in one small area. A spotless engine
                            bay is not always a good sign if it looks like someone cleaned away
                            evidence of leaks.
                        </p>
                    </section>

                    <section id="check-6" className="mb-11 pt-[1px]">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">6. Check Engine Oil and Other Fluids</h2>
                        <p className="m-0 text-gray-700">
                            Pull the oil dipstick if available and inspect the oil level and condition.
                            Also check coolant, brake fluid, and washer fluid where possible. Very
                            dirty oil, low coolant, or unusually contaminated fluid can suggest poor
                            upkeep.
                        </p>
                    </section>

                    <figure className="mb-10">
                        <img
                            src="/blogs/6_3.jpeg"
                            alt="Checking the engine bay of a used car"
                            title="Used car engine inspection"
                            className="w-full h-auto block border border-gray-300 rounded-xl"
                        />
                        <figcaption className="text-xs text-gray-400 mt-2">
                            Inline image: used_car_engine_check.png
                        </figcaption>
                    </figure>

                    <section id="check-7" className="mb-11 pt-[1px]">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">7. Inspect the Battery Condition</h2>
                        <p className="m-0 text-gray-700">
                            Check the battery terminals for corrosion and pay attention to whether the
                            car starts quickly and smoothly. A weak battery may be a small fix, but it
                            can also hint that the electrical system has not been maintained well.
                        </p>
                    </section>

                    <section id="check-8" className="mb-11 pt-[1px]">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">8. Check the Interior Condition</h2>
                        <p className="m-0 text-gray-700">
                            Look closely at the seats, steering wheel, pedals, door trim, and carpets.
                            Excessive wear inside the cabin can sometimes tell a different story than
                            the odometer. A car with low mileage but heavily worn controls deserves a
                            closer look.
                        </p>
                    </section>

                    <section id="check-9" className="mb-11 pt-[1px]">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">9. Test the AC and Electronics</h2>
                        <p className="m-0 text-gray-700">
                            Turn on the air conditioning, blower fan, infotainment system, windows,
                            locks, mirrors, and any other major controls. Electrical issues can be
                            annoying, expensive, and easy to miss if you rush through the inspection.
                        </p>
                    </section>

                    <section id="check-10" className="mb-11 pt-[1px]">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">10. Check for Dashboard Warning Lights</h2>
                        <p className="m-0 text-gray-700">
                            When the ignition is turned on, dashboard lights should appear briefly and
                            then switch off once the car starts. If a warning light stays on,
                            especially for the engine, battery, brakes, or temperature, it may point
                            to a real issue.
                        </p>
                    </section>

                    <section id="check-11" className="mb-11 pt-[1px]">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">11. Inspect the Brakes</h2>
                        <p className="m-0 text-gray-700">
                            During the drive, pay attention to braking feel. The pedal should feel firm
                            and the car should stop evenly. Squeaking, grinding, shaking, or pulling
                            while braking can all suggest work is needed.
                        </p>
                    </section>

                    <section id="check-12" className="mb-11 pt-[1px]">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">12. Check Suspension and Steering</h2>
                        <p className="m-0 text-gray-700">
                            Drive over a few bumps if possible and notice whether the car feels stable
                            or unusually bouncy. Clunks, knocking noises, loose steering, or vibration
                            can point to suspension or steering wear.
                        </p>
                    </section>

                    <section id="check-13" className="mb-11 pt-[1px]">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">13. Compare Mileage with Overall Wear</h2>
                        <p className="m-0 text-gray-700">
                            Mileage matters, but it should make sense with the car’s overall condition.
                            A lower-mileage car with heavy seat wear, worn pedals, and a tired steering
                            wheel may deserve extra questions.
                        </p>
                    </section>

                    <section id="check-14" className="mb-11 pt-[1px]">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">14. Review the Service History</h2>
                        <p className="m-0 text-gray-700">
                            Service history can tell you whether the car has been consistently
                            maintained or only fixed when something went wrong. Look for regular oil
                            changes, brake work, tire replacement, and major scheduled maintenance.
                        </p>
                    </section>

                    <section id="check-15" className="mb-11">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">15. Take a Proper Test Drive</h2>
                        <p className="m-0 text-gray-700">
                            A real test drive should include slow driving, normal acceleration,
                            braking, and a slightly higher-speed section if possible. Listen for
                            unusual noises, feel for vibration, check gear changes, and notice whether
                            the car tracks straight.
                        </p>
                    </section>

                    <section id="dash-section" className="mb-10 overflow-hidden">
                        <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 shadow-sm">
                            <h2 className="text-2xl leading-tight mb-3.5 text-gray-900 font-bold">
                                Evaluate a Used Car with CarFido
                            </h2>

                            <p className="mb-3.5 text-gray-700">
                                Buying a used car gets easier when you know what questions to ask and what
                                warning signs to notice.
                            </p>

                            <p className="mb-3.5 text-gray-700">
                                CarFido can help you understand warning lights, think through
                                symptoms, and evaluate whether something you noticed on the car may be
                                minor or worth investigating further.
                            </p>

                            <p className="mb-2.5 text-gray-900 font-bold">You can ask questions such as:</p>

                            <ul className="pl-6 m-0 list-disc space-y-2 text-gray-700">
                                <li className="mb-1">Is this used car showing signs of neglect?</li>
                                <li className="mb-1">Should I worry about uneven tire wear or a warning light?</li>
                            </ul>
                        </div>
                    </section>

                    <section id="faq" className="mb-11 border-t border-gray-200 pt-8">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-5 text-gray-900 font-bold">
                            Frequently Asked Questions
                        </h2>

                        <div className="space-y-1">
                            <FAQitem
                                question="What should I check before buying a used car?"
                                answer="Check the exterior, tires, engine bay, fluids, battery, brakes, suspension, dashboard lights, electronics, service records, and how the car behaves during a test drive."
                            />
                            <FAQitem
                                question="Is a test drive necessary when buying a used car?"
                                answer="Yes. A test drive helps reveal brake issues, steering problems, unusual noises, vibration, and other problems that may not be obvious while the car is parked."
                            />
                            <FAQitem
                                question="Can a used car look fine but still have hidden issues?"
                                answer="Yes. Cosmetic condition does not always reflect the true mechanical condition of the car, which is why a careful inspection is important."
                            />
                            <FAQitem
                                question="How important is service history when buying a used car?"
                                answer="Service history is very important because it shows whether the car was maintained consistently over time."
                            />
                            <FAQitem
                                question="Can CarFido help me evaluate a used car?"
                                answer="Yes. CarFido can help you understand warning signs, think through symptoms, and ask smarter questions before buying."
                            />
                        </div>
                    </section>

                    <section id="final-thoughts" className="mb-11">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">
                            Final Thoughts
                        </h2>

                        <p className="mb-4 text-gray-700">
                            Buying a used car does not have to feel like a gamble. The more carefully
                            you inspect the car, the better your chances of finding a vehicle that is
                            actually worth the money.
                        </p>

                        <p className="mb-4 text-gray-700">
                            A few extra minutes spent checking the tires, body, fluids, warning
                            lights, and service history can protect you from repairs that cost far
                            more than the discount you thought you were getting.
                        </p>

                        <p className="m-0 text-gray-700">
                            Before you make a final decision, slow down, inspect the car properly, and
                            trust what the condition tells you — not just what the seller says.
                        </p>
                    </section>
                </main>

                <aside className="w-full lg:w-[300px] lg:flex-[0_0_300px] lg:sticky lg:top-5 self-start space-y-5">
                    <div className="border border-gray-200 rounded-xl bg-white p-5 shadow-sm">
                        <div className="text-base font-bold text-gray-900 mb-3">
                            Quick Navigation
                        </div>

                        <ul className="list-none p-0 m-0 space-y-2.5">
                            <li><a href="#quick-answer" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Quick answer</a></li>
                            <li><a href="#inspection-table" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Checklist summary</a></li>
                            <li><a href="#why-it-matters" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Why it matters</a></li>
                            <li><a href="#check-1" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Exterior</a></li>
                            <li><a href="#check-2" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Tires</a></li>
                            <li><a href="#check-5" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Engine bay</a></li>
                            <li><a href="#check-10" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Warning lights</a></li>
                            <li><a href="#check-15" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Test drive</a></li>
                            <li><a href="#faq" className="no-underline text-gray-600 hover:text-gray-900 text-sm">FAQ</a></li>
                        </ul>
                    </div>

                    <div className="border border-gray-200 rounded-xl bg-gray-50 p-5 shadow-sm">
                        <div className="text-base font-bold text-gray-900 mb-2.5">
                            Related Reads
                        </div>
                        {(() => {
                            const otherBlogs = blogs.filter(b => b.id !== 6);
                            const startIndex = 6 % otherBlogs.length;
                            return [...otherBlogs.slice(startIndex), ...otherBlogs.slice(0, startIndex)].slice(0, 3);
                        })().map((relatedBlog) => (
                            <Link 
                                key={relatedBlog.id}
                                to={relatedBlog.path} 
                                className="block no-underline text-gray-900 mb-3 text-sm leading-snug font-medium hover:text-gray-600 transition-colors"
                            >
                                {relatedBlog.title}
                            </Link>
                        ))}
                    </div>
                </aside>
            </div>
        </div>
    );
};

export default Blog6;